import {createApi} from "@reduxjs/toolkit/query/react";
import {axiosBaseQuery} from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import {appendFormData} from "@/helpers/form";

export const speakersApi = createApi({
    reducerPath: 'speakersApi',
    tagTypes: ['speakers'],
    baseQuery: axiosBaseQuery({
        baseUrl: api.root,
    }),
    endpoints: build => ({
        getSpeakers: build.query({
            query: (data) => ({
                url: api.speakers.getSpeakers(),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return [endpointName, queryArgs?.location_id]
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg !== previousArg;
            },
            providesTags: ['speakers']
        }),
        getSpeakersOptions: build.query({
            query: (data) => ({
                url: api.speakers.getSpeakers(),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return [endpointName + 'Options', queryArgs?.location_id + 'Options']
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg !== previousArg;
            },
            providesTags: ['speakers']
        }),
        createSpeaker: build.mutation({
            query: (data) => {
                const formData = new FormData();
                appendFormData(formData, data);

                return {
                    url: api.speakers.createSpeaker(),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data;'
                    },
                    body: formData,
                    formData: true
                };
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        speakersApi.util.updateQueryData('getSpeakers', patch, (draft) => {
                            draft.unshift(data)
                        })
                    )
                } catch (error) {
                    console.error('Failed to update cache:', error)
                }
            },
        }),
        updateSpeaker: build.mutation({
            query: ({id, ...data}) => {
                const formData = new FormData();
                appendFormData(formData, data);
                formData.append('_method', 'PUT')

                return {
                    url: api.speakers.updateSpeaker(id),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data;'
                    },
                    body: formData,
                    formData: true
                };
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        speakersApi.util.updateQueryData('getSpeakers', {location_id: patch.location_id}, (draft) => {
                            const itemIndex = draft?.findIndex(item => item.id === patch.id);
                            if (itemIndex !== -1) {
                                draft[itemIndex] = data;
                            }
                        })
                    );
                } catch (e) {
                    console.error('Update failed:', e);
                }
            },
        }),
        deleteSpeaker: build.mutation({
            query: ({id, ...data}) => ({
                url: api.speakers.deleteSpeaker(id),
                method: 'DELETE',
                body: data
            }),
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        speakersApi.util.updateQueryData('getSpeakers', patch, (draft) => {
                            const index = draft.findIndex((speaker) => speaker.id === patch.id);
                            if (index !== -1) {
                                draft.splice(index, 1)
                            } else {
                                console.warn('Speaker not found in cache')
                            }
                        })
                    );
                } catch (e) {
                    console.error('Delete failed:', e)
                }
            },
        }),
        getSpeakerHistory: build.query({
            query: ({id, ...data}) => ({
                url: api.speakers.getSpeakerHistory(id),
                method: 'GET',
                params: data,
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id, queryArgs?.storage_id]
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
        }),
    })
})

export const { useLazyGetSpeakersOptionsQuery, useDeleteSpeakerMutation } = speakersApi